/** Sensible RAG retrieval clamps shared by chatbot config and platform chat. */

export const RAG_MAX_RESULTS_MIN = 1;
export const RAG_MAX_RESULTS_MAX = 20;
/** Matches the Balanced preset. */
export const RAG_MAX_RESULTS_DEFAULT = 10;
export const RAG_MAX_RESULTS_STEP = 1;

export const RAG_MIN_SCORE_MIN = 0;
export const RAG_MIN_SCORE_MAX = 1;
/** Matches the Balanced preset. */
export const RAG_MIN_SCORE_DEFAULT = 0.5;
export const RAG_MIN_SCORE_STEP = 0.05;

export const RAG_RETRIEVAL_PRESET_IDS = [
  'focused',
  'balanced',
  'broad',
] as const;

export type RagRetrievalPresetId = (typeof RAG_RETRIEVAL_PRESET_IDS)[number];

export type RagRetrievalPresetValues = {
  maxResults: number;
  minScore: number;
};

export const RAG_RETRIEVAL_PRESETS: Record<
  RagRetrievalPresetId,
  RagRetrievalPresetValues
> = {
  focused: { maxResults: 5, minScore: 0.65 },
  balanced: { maxResults: 10, minScore: 0.5 },
  broad: { maxResults: 20, minScore: 0.35 },
};

export const RAG_RETRIEVAL_DEFAULT_PRESET: RagRetrievalPresetId = 'balanced';

function snapToStep(value: number, min: number, step: number): number {
  const steps = Math.round((value - min) / step);
  return Number((min + steps * step).toFixed(10));
}

export function clampRagMaxResults(value: number): number {
  if (!Number.isFinite(value)) {
    return RAG_MAX_RESULTS_DEFAULT;
  }

  const snapped = snapToStep(value, RAG_MAX_RESULTS_MIN, RAG_MAX_RESULTS_STEP);
  return Math.min(
    RAG_MAX_RESULTS_MAX,
    Math.max(RAG_MAX_RESULTS_MIN, Math.round(snapped)),
  );
}

export function clampRagMinScore(value: number): number {
  if (!Number.isFinite(value)) {
    return RAG_MIN_SCORE_DEFAULT;
  }

  const snapped = snapToStep(value, RAG_MIN_SCORE_MIN, RAG_MIN_SCORE_STEP);
  return Math.min(RAG_MIN_SCORE_MAX, Math.max(RAG_MIN_SCORE_MIN, snapped));
}

export function resolveRagMaxResults(value: number | null | undefined): number {
  return value == null ? RAG_MAX_RESULTS_DEFAULT : clampRagMaxResults(value);
}

export function resolveRagMinScore(value: number | null | undefined): number {
  return value == null ? RAG_MIN_SCORE_DEFAULT : clampRagMinScore(value);
}

export function formatRagMinScore(value: number): string {
  return clampRagMinScore(value).toFixed(2);
}

export function matchRagRetrievalPreset(
  maxResults: number,
  minScore: number,
): RagRetrievalPresetId | null {
  const clampedMax = clampRagMaxResults(maxResults);
  const clampedMin = clampRagMinScore(minScore);

  for (const id of RAG_RETRIEVAL_PRESET_IDS) {
    const preset = RAG_RETRIEVAL_PRESETS[id];
    if (preset.maxResults === clampedMax && preset.minScore === clampedMin) {
      return id;
    }
  }

  return null;
}

export function isRagRetrievalPresetId(
  value: string,
): value is RagRetrievalPresetId {
  return RAG_RETRIEVAL_PRESET_IDS.some((id) => id === value);
}
